import { ArrowRight, ArrowUpRight, Globe, GraduationCap, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  MediaFrame,
  NavyAtmosphere,
  Parallax,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { brand } from '@/config/brand';
import {
  PROGRAMMES_BY_TYPE,
  programmeImage,
  type Programme,
  type ProgrammeType,
} from '@/data/programmes';
import { STAGGER } from '@/lib/motion-tokens';
import { Badge, Button, Card, cn } from '@ibas/ui';

/**
 * ProgrammeCard — a single premium programme tile. The WHOLE card is a router
 * Link to the detail page; a shared TiltCard leans the surface toward the
 * cursor (pointer-driven, guarded for touch/reduced-motion inside the
 * primitive), and a MediaFrame photo header keeps the portfolio visual rather
 * than purely typographic. The retention figure is rendered ONLY where
 * programme.acbsp is true (MBA & DBA) and always carries its year + scope +
 * source caption, per the compliance claims rule.
 */
function ProgrammeCard({ programme }: { programme: Programme }) {
  const isOnline = programme.type === 'Online';
  const ModeIcon = isOnline ? Globe : GraduationCap;
  const facts = programme.facts.slice(0, 2);

  return (
    <Link
      to={`/programmes/${programme.slug}`}
      data-cursor
      className="group block h-full rounded-xl focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
      aria-label={`${programme.code} — ${programme.title}`}
    >
      <TiltCard className="h-full" max={6}>
        <Card className="relative flex h-full flex-col overflow-hidden border-border p-0 shadow-sm transition-shadow duration-500 group-hover:shadow-xl motion-reduce:transition-none">
          {/* Gold sheen that sweeps across the surface on hover. */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 -translate-x-full bg-gradient-to-r from-transparent via-brand-gold/15 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full motion-reduce:hidden"
          />

          <MediaFrame
            image={programmeImage(programme)}
            alt={programme.alt}
            position={programme.imagePosition}
            aspect="aspect-[16/10]"
            rounded="rounded-none"
            reveal={false}
            className="shrink-0"
          />

          <div className="relative flex flex-1 flex-col gap-4 border border-t-0 border-transparent p-5 transition-colors duration-500 group-hover:border-brand-gold/30 motion-reduce:transition-none">
            <div className="flex items-start justify-between gap-4">
              <Badge
                variant={isOnline ? 'secondary' : 'default'}
                className="w-fit font-label tracking-wider uppercase"
              >
                <ModeIcon aria-hidden className="size-3.5" />
                {isOnline ? 'Online' : 'On-site'}
                {programme.location ? ` · ${programme.location}` : ''}
              </Badge>
              <ArrowUpRight
                aria-hidden
                className="size-5 shrink-0 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
              />
            </div>

            <div>
              <p className="font-label text-xs tracking-[0.18em] text-brand-gold uppercase">
                {programme.code}
              </p>
              <h3 className="mt-1.5 font-display text-2xl leading-tight font-semibold text-foreground">
                {programme.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {programme.summary}
              </p>
            </div>

            <dl className="mt-auto grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-4">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <dt className="font-label text-[0.65rem] tracking-[0.14em] text-muted-foreground uppercase">
                    {fact.label}
                  </dt>
                  <dd className="mt-0.5 text-sm text-foreground">{fact.value}</dd>
                </div>
              ))}
            </dl>

            {/* Retention: MBA & DBA only, with the mandated year + scope + source caption. */}
            {programme.acbsp && programme.retention2025 ? (
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{programme.retention2025}</span>{' '}
                student retention 2025 (ACBSP)
              </p>
            ) : null}
          </div>
        </Card>
      </TiltCard>
    </Link>
  );
}

interface ProgrammeGroupProps {
  eyebrow: string;
  title: string;
  lede: string;
  type: ProgrammeType;
  className?: string;
  /** Optional trailing content (e.g. the online-learning introduction link). */
  aside?: React.ReactNode;
}

/**
 * ProgrammeGroup — one delivery-type block (Online / On-site). The heading
 * enters as a SplitReveal; the card grid is wrapped in a single ScrollReveal so
 * the tiles rise in a staggered procession as the block scrolls into view
 * (scroll-driven, rect-based, reduced-motion safe via the primitive).
 */
function ProgrammeGroup({ eyebrow, title, lede, type, className, aside }: ProgrammeGroupProps) {
  const programmes = PROGRAMMES_BY_TYPE[type];

  return (
    <section className={cn('bg-background py-16 md:py-24', className)}>
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">{eyebrow}</p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            {title}
          </SplitReveal>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">{lede}</p>
        </header>

        <ScrollReveal
          as="div"
          from="up"
          stagger={STAGGER.base}
          className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {programmes.map((programme) => (
            <ProgrammeCard key={programme.slug} programme={programme} />
          ))}
        </ScrollReveal>

        {aside ? <div className="mt-8">{aside}</div> : null}
      </div>
    </section>
  );
}

/**
 * ProgrammesPage (/programmes) — the programme portfolio overview.
 *
 * Composition: a navy hero band (NavyAtmosphere backdrop + a pointer-parallax
 * media collage) states the campaign line and routes to Apply / an adviser;
 * two grouped blocks (Online Flexi-Learn, then On-site Thalwil) render every
 * programme from PROGRAMMES_BY_TYPE as a linked premium card; a "why IBAS" band
 * surfaces the promise + FLEXI/EDU/CARE framework; and a navy closing CTA band
 * leads into the shared enquiry form.
 *
 * Motion: SplitReveal headings, ScrollReveal staggered card grids, TiltCard on
 * each tile, PointerParallax + Parallax in the hero — all reduced-motion safe
 * and driven through the shared primitives so nothing is ever stranded hidden.
 * Content and colour follow the ibas-brand skill (semantic tokens only; UK
 * English; no invented facts; retention shown only on ACBSP programmes).
 */
/**
 * Hero-collage plates — hand-picked, orientation-matched IBAS stills so each
 * tile fills its frame under object-cover: two portrait people shots for the
 * tall 4/5 tiles, and the square DBA promo (from programme data) for the square
 * tile.
 */
const COLLAGE_PORTRAIT_A = '/media/images/people/ibas-student-portrait-snow.webp';
const COLLAGE_PORTRAIT_C = '/media/images/people/faculty-presenting-projector-screen.webp';

export function ProgrammesPage() {
  const heroImageB = PROGRAMMES_BY_TYPE.Online[1];

  return (
    <>
      {/* HERO ------------------------------------------------------------- */}
      <section className="relative isolate overflow-hidden bg-brand-navy">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 md:py-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              as="p"
              text="Programmes"
              className="font-label text-xs tracking-[0.28em] text-brand-gold uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-5 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              {brand.campaignLine}
            </SplitReveal>
            <p className="mt-6 text-lg leading-relaxed text-white/75">{brand.essence}</p>
            <p className="mt-3 text-base leading-relaxed text-white/65">
              {brand.promise.join(' ')} Choose a fully online Flexi-Learn route or on-site study at
              our campus in Thalwil, Switzerland.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button asChild variant="secondary" size="lg">
                <Link to="/apply">Start your application</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/admissions#find-advisor">Connect with an adviser</Link>
              </Button>
            </div>
          </div>

          {/* Media collage — a pointer-driven parallax trio; disabled on touch
              and reduced motion inside the primitive. */}
          <PointerParallax strength={18} className="hidden lg:block">
            <div className="relative h-[30rem]">
              <div data-depth="0.9" className="absolute top-0 right-0 w-[62%]">
                <TiltCard max={7}>
                  <MediaFrame
                    image={COLLAGE_PORTRAIT_A}
                    alt="An IBAS student standing outside the Thalwil campus during a light Swiss snowfall."
                    aspect="aspect-[4/5]"
                    overlay={35}
                    rounded="rounded-2xl"
                    position="50% 30%"
                  />
                </TiltCard>
              </div>
              <div data-depth="1.4" className="absolute bottom-0 left-0 w-[56%]">
                <TiltCard max={7}>
                  <MediaFrame
                    image={COLLAGE_PORTRAIT_C}
                    alt="A presenter leading a small-group class beside a projector screen in a sunlit IBAS classroom."
                    aspect="aspect-[4/5]"
                    overlay={35}
                    rounded="rounded-2xl"
                    position="50% 45%"
                  />
                </TiltCard>
              </div>
              <div data-depth="0.5" className="absolute top-1/3 left-1/4 w-[38%]">
                <TiltCard max={7}>
                  <MediaFrame
                    image={programmeImage(heroImageB, 700)}
                    alt={heroImageB.alt}
                    position={heroImageB.imagePosition}
                    aspect="aspect-square"
                    overlay={35}
                    rounded="rounded-2xl"
                  />
                </TiltCard>
              </div>
            </div>
          </PointerParallax>
        </div>
      </section>

      {/* ONLINE ----------------------------------------------------------- */}
      <ProgrammeGroup
        eyebrow="Online — Flexi-Learn"
        title="Study fully online, paced around your career"
        lede="Our online programmes follow the Flexi-Learn model — rolling monthly intakes and self-study, live online, asynchronous and blended delivery, so you can begin when it suits you and study around work and life."
        type="Online"
        aside={
          <Link
            to="/programmes/online-learning"
            data-cursor
            className="group inline-flex items-center gap-2 text-base font-medium text-foreground underline decoration-brand-gold/50 decoration-2 underline-offset-4 transition-colors hover:text-brand-gold focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
          >
            New to online study? An introduction to online learning
            <ArrowRight
              aria-hidden
              className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none"
            />
          </Link>
        }
      />

      {/* ON-SITE ---------------------------------------------------------- */}
      <ProgrammeGroup
        eyebrow="On-site — Thalwil, Switzerland"
        title="Study on-site at our Swiss campus"
        lede="Our on-site programmes are studied at the IBAS campus in Thalwil, Switzerland, on scheduled intakes. Speak with an adviser for current intake dates, entry requirements and structure."
        type="On-site"
        className="bg-muted/30"
        aside={
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin aria-hidden className="size-4 text-brand-gold" />
            Campus: Thalwil, Switzerland
          </p>
        }
      />

      {/* WHY IBAS --------------------------------------------------------- */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Why study with IBAS
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              {brand.institutionalTagline}
            </SplitReveal>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              {brand.promise.join(' ')}
            </p>
          </header>

          <ScrollReveal
            as="div"
            from="up"
            stagger={STAGGER.base}
            className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3"
          >
            {brand.framework.map((pillar) => (
              <div
                key={pillar.key}
                className="rounded-xl border border-border bg-card p-7 transition-colors duration-500 hover:border-brand-gold/40 motion-reduce:transition-none"
              >
                <p className="font-display text-4xl font-semibold text-brand-gold">{pillar.key}</p>
                <p className="mt-3 text-lg leading-relaxed text-foreground">{pillar.label}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* CLOSING CTA ------------------------------------------------------ */}
      <section className="relative isolate overflow-hidden bg-brand-navy">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-6 py-20 text-center md:py-28">
          <Parallax speed={0.12}>
            <SplitReveal
              as="h2"
              by="lines"
              className="font-display text-3xl leading-tight text-white md:text-5xl"
            >
              Find your programme. Start when you are ready.
            </SplitReveal>
          </Parallax>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white/75">
            Whichever route fits your life, an adviser can help you choose and guide you through
            entry requirements and next steps.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Button asChild variant="secondary" size="lg">
              <Link to="/apply">Start your application</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/admissions#find-advisor">Connect with an adviser</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ENQUIRY — the shared request-information form (as on the home page) --- */}
      <EnquiryForm />
    </>
  );
}
